import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  useScrollTrigger,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useThemeContext } from "../contexts/ThemeContext.tsx";
import logo from "../assets/images/logo.png";
import MobileMenu from "./MobileMenu.tsx";

const StyledAppBar = styled(AppBar)<{ $triggered: boolean; $isHomePage: boolean; component?: React.ElementType }>(({ theme, $triggered, $isHomePage }) => ({
  position: 'fixed',
  backgroundColor: $triggered || !$isHomePage ? theme.palette.background.paper : 'transparent',
  boxShadow: $triggered || !$isHomePage ? theme.shadows[1] : 'none',
  transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
  borderRadius: '0 0 12px 12px',
}));

const StyledContainer = styled(Container)(() => ({
  maxWidth: 'lg',
}));

const StyledToolbar = styled(Toolbar)({
  justifyContent: 'space-between',
  minHeight: 72,
});

const StyledLogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const StyledLogo = styled('img')(({ theme }) => ({
  height: 50,
  marginRight: theme.spacing(2),
}));

const StyledLogoText = styled(Typography)<{ $textColor: string; component?: React.ElementType }>(({ theme, $textColor }) => ({
  display: 'none',
  fontWeight: 'bold',
  color: $textColor,
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
}));

const StyledMobileMenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const StyledDesktopMenuContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  gap: theme.spacing(0.25),
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledNavButton = styled(Button)<{ $textColor: string }>(({ theme, $textColor }) => ({
  margin: theme.spacing(1, 0),
  color: $textColor,
  fontWeight: 'bold',
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
}));

const StyledThemeToggle = styled(IconButton)<{ $textColor: string }>(({ theme, $textColor }) => ({
  marginRight: theme.spacing(0.5),
  color: $textColor,
}));

const StyledPortalButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  paddingLeft: theme.spacing(1.75),
  paddingRight: theme.spacing(1.75),
  whiteSpace: 'nowrap',
}));

const StyledBookButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  whiteSpace: 'nowrap',
}));

interface NavbarProps {
  onOpenBookingDialog: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenBookingDialog }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { toggleColorMode, mode } = useThemeContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === "/";

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleOpenMobileMenu = () => setMobileMenuOpen(true);
  const handleCloseMobileMenu = () => setMobileMenuOpen(false);

  const scrollTo = (id: string) => {
    handleCloseMobileMenu();

    if (!isHomePage) {
      window.location.href = `/#${id}`;
      return;
    }

    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const getTextColor = () => {
    if (trigger || !isHomePage) return "text.primary";
    return "primary.main";
  };

  return (
    <StyledAppBar
      $triggered={trigger}
      $isHomePage={isHomePage}
      component="nav"
      aria-label="Hoofdnavigatie"
    >
      <StyledContainer>
        <StyledToolbar disableGutters>
          <StyledLogoContainer
            onClick={() => navigate('/')}
            role="button"
            tabIndex={0}
            aria-label="Ga naar de homepage"
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') navigate('/');
            }}
          >
            <StyledLogo src={logo} alt="Pippaya Logo" role="img" />
            <StyledLogoText
              variant="h6"
              noWrap
              component="div"
              $textColor={getTextColor()}
            >
              Pippaya
            </StyledLogoText>
          </StyledLogoContainer>

          <StyledMobileMenuContainer>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenMobileMenu}
              color="inherit"
              sx={{ color: getTextColor() }}
            >
              <MenuIcon />
            </IconButton>
            <MobileMenu
              open={mobileMenuOpen}
              onClose={handleCloseMobileMenu}
              onOpenBookingDialog={onOpenBookingDialog}
              scrollTo={scrollTo}
            />
          </StyledMobileMenuContainer>

          <StyledDesktopMenuContainer>
            <StyledThemeToggle
              onClick={toggleColorMode}
              color="inherit"
              aria-label={
                mode === "dark"
                  ? "Schakel naar lichte modus"
                  : "Schakel naar donkere modus"
              }
              $textColor={getTextColor()}
            >
              {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
            </StyledThemeToggle>

            <StyledNavButton onClick={() => scrollTo("home")} $textColor={getTextColor()}>
              Home
            </StyledNavButton>
            <StyledNavButton onClick={() => scrollTo("about")} $textColor={getTextColor()}>
              Over ons
            </StyledNavButton>
            <StyledNavButton onClick={() => scrollTo("services")} $textColor={getTextColor()}>
              Diensten
            </StyledNavButton>

            <StyledPortalButton
              variant="outlined"
              color="primary"
              startIcon={<PersonOutlineRoundedIcon />}
              onClick={() => navigate('/inloggen')}
            >
              Mijn Pippaya
            </StyledPortalButton>

            <StyledBookButton
              variant="contained"
              color="secondary"
              onClick={onOpenBookingDialog}
            >
              Reservering aanvragen
            </StyledBookButton>
          </StyledDesktopMenuContainer>
        </StyledToolbar>
      </StyledContainer>
    </StyledAppBar>
  );
};

export default Navbar;
